import { useState } from "react";
import useSWRMutation from "swr/mutation";
import { ChevronDownIcon, PlusIcon } from "lucide-react";
import { Section } from "@/components/section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  allChannelStateKeys,
  channelFlowStart,
  ChannelStateChip,
  ChannelStates,
  defaultChannelFlow,
} from "@/components/badge/channel-state";
import { useChannelStates } from "@/hooks/channel-states";
import { useIsAdmin } from "@/hooks/auth";
import { STATE_COLOR_TONES, toneClass } from "@/constants/version-state";
import { mutateRequest, postFetcher, putFetcher } from "@/lib/swr";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { cn } from "@/lib/utils";
import type { App } from "@/types/domain";
import { refreshApps } from "./refresh-apps";
import { ChannelFlowGraph } from "./channel-flow-graph";

type CFlow = Record<string, string[]>;

// Stable serialization for dirty/default checks (catalog order, enabled keys
// only; nexts sorted; the reserved `start` entry rides along first).
const serialize = (flow: CFlow) =>
  JSON.stringify([
    channelFlowStart(flow),
    ...allChannelStateKeys()
      .filter((s) => s in flow)
      .map((s) => [s, [...(flow[s] ?? [])].sort()]),
  ]);

// States reachable from the configured entry by walking nexts.
const reachableSet = (flow: CFlow): Set<string> => {
  const entry = channelFlowStart(flow);
  const seen = new Set<string>([entry]);
  const queue = [entry];
  while (queue.length) {
    const s = queue.shift() as string;
    for (const n of flow[s] ?? []) {
      if (!seen.has(n)) {
        seen.add(n);
        queue.push(n);
      }
    }
  }
  return seen;
};

// Drop everything the start can no longer reach (their nexts only pointed
// inside the pruned group, so the remaining graph stays self-contained).
const prune = (flow: CFlow): CFlow => {
  const reach = reachableSet(flow);
  const out: CFlow = { start: [channelFlowStart(flow)] };
  for (const k of Object.keys(flow)) {
    if (k !== "start" && reach.has(k)) out[k] = flow[k];
  }
  return out;
};

// Create one global-catalog channel state (channel_state table). Key is
// immutable once created (it is the PK and lives inside channel rows and app
// flows); wire an arrow to the new state to pull it into this app's flow.
// Writes are admin-gated on the server.
const NewStateDialog = ({ onClose }: { onClose: () => void }) => {
  const { toast } = useToast();
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [color, setColor] = useState("none");

  const { trigger, isMutating } = useSWRMutation(
    "/channel-states",
    postFetcher,
    {
      onSuccess: () => {
        mutateRequest("/channel-states");
        onClose();
      },
      onError: (err: unknown) => toastError(toast, "新建状态失败", err),
    }
  );

  const submit = () => {
    if (!key.trim() || !label.trim() || isMutating) return;
    trigger({
      key: key.trim(),
      label: label.trim(),
      color: color === "none" ? null : color,
      nexts: [],
    });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>新建渠道状态</DialogTitle>
          <DialogDescription>
            写入全局目录(所有 App 共用,key 建后不可改);建好后在某个状态的
            nexts 里指一条边过去,即拉入本 App 的流转图。
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block space-y-1">
            <span className="text-sm text-muted-foreground">
              key(小写英文标识)
            </span>
            <Input
              value={key}
              placeholder="e.g. paused"
              autoFocus
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setKey(e.target.value)
              }
              className="h-8 font-mono text-xs"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm text-muted-foreground">显示名</span>
            <Input
              value={label}
              placeholder="e.g. 暂停放量"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setLabel(e.target.value)
              }
              className="h-8"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm text-muted-foreground">色调</span>
            <Select value={color} onValueChange={setColor}>
              <SelectTrigger size="sm" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">缺省灰</SelectItem>
                {STATE_COLOR_TONES.map((t) => (
                  <SelectItem key={t} value={t}>
                    <span
                      className={cn(
                        "rounded px-1.5 py-0.5 text-xs font-medium",
                        toneClass(t)
                      )}
                    >
                      {t}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            取消
          </Button>
          <Button
            size="sm"
            disabled={isMutating || !key.trim() || !label.trim()}
            onClick={submit}
          >
            {isMutating ? "创建中…" : "创建"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Per-app channel-flow editor, modeled on VersionFlowSection: the state
// roster comes from the global channel_state catalog (admins create new
// states right here) — configurable are the Start entry (where a newly
// added channel lands) and each state's nexts, 1:1 with the graph (the
// buttons ops get on the channel page). Unreachable states prune away.
// Release logs only record rollout progress and never move state. Saving
// the default graph stores null.
export const ChannelFlowSection = ({ app }: { app: App }) => {
  const { toast } = useToast();
  // Re-render when the runtime catalog loads/changes (DB-managed states).
  useChannelStates();
  const isAdmin = useIsAdmin();
  const [addingState, setAddingState] = useState(false);
  const defFlow = defaultChannelFlow();
  const saved: CFlow = app.channelStateFlow
    ? { start: [channelFlowStart(app.channelStateFlow)], ...app.channelStateFlow }
    : defFlow;
  // null = no local edits yet; render the saved config.
  const [draft, setDraft] = useState<CFlow | null>(null);
  const flow = draft ?? saved;
  const entry = channelFlowStart(flow);
  const dirty = serialize(flow) !== serialize(saved);
  const isDefault = serialize(flow) === serialize(defFlow);

  const { trigger, isMutating } = useSWRMutation(
    `/apps/${app.id}/channel-flow`,
    putFetcher,
    {
      onSuccess: () => {
        refreshApps();
        setDraft(null);
        toast({ title: "Channel flow 已保存" });
      },
      onError: (err: unknown) => toastError(toast, "保存失败", err),
    }
  );

  const toggleNext = (state: string, target: string) => {
    const curr = flow[state] ?? [];
    if (curr.includes(target)) {
      // Removing an arrow may strand states — prune what Start can't reach.
      setDraft(prune({ ...flow, [state]: curr.filter((n) => n !== target) }));
    } else {
      const next: CFlow = {
        ...flow,
        [state]: [...curr, target].sort(),
      };
      // First arrow into a state pulls it into the flow as a fresh row.
      if (!(target in next)) next[target] = [];
      setDraft(next);
    }
  };

  const setStart = (s: string) => {
    const next: CFlow = { ...flow, start: [s] };
    // The new entry must exist as a row before pruning walks from it.
    if (!(s in next)) next[s] = [];
    setDraft(prune(next));
  };

  const stateKeys = allChannelStateKeys();
  // Rows: enabled (reachable) states only, in catalog order.
  const rows = stateKeys.filter((s) => s in flow);

  return (
    <Section
      title="Channel flow"
      action={
        <div className="flex items-center gap-2">
          {/* Creating here writes the global catalog (keys mean the same
              thing in every app); wire an arrow to the new state to pull it
              into this app's flow, then Save. */}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => setAddingState(true)}
            >
              <PlusIcon className="mr-1 h-3.5 w-3.5" />
              New state
            </Button>
          )}
          {!isDefault && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8"
              disabled={isMutating}
              onClick={() => setDraft(defFlow)}
            >
              恢复默认
            </Button>
          )}
          <Button
            size="sm"
            className="h-8"
            disabled={!dirty || isMutating}
            onClick={() => trigger({ flow: isDefault ? null : flow })}
          >
            {isMutating ? "保存中…" : "保存"}
          </Button>
        </div>
      }
    >
      <div className="mt-2">
        <ChannelFlowGraph flow={flow} />
      </div>
      <div className="mt-3 flex flex-col gap-1">
        {/* Virtual Start node: where a newly added channel lands. */}
        <div className="flex items-center gap-3 py-0.5">
          <span className="w-10 shrink-0" />
          <span className="w-16 shrink-0 rounded bg-primary px-1.5 py-0.5 text-center text-xs font-medium text-primary-foreground">
            Start
          </span>
          <span className="w-20 shrink-0" />
          <div className="flex min-w-0 items-center gap-2">
            <span className="text-xs text-muted-foreground">→</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 max-w-72 justify-between gap-1 px-2 text-xs font-normal"
                >
                  <span className="truncate">
                    {ChannelStates[entry]?.label ?? entry}
                  </span>
                  <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                <DropdownMenuLabel className="text-xs">
                  新建渠道落在
                </DropdownMenuLabel>
                {stateKeys.map((s) => (
                  <DropdownMenuCheckboxItem
                    key={s}
                    checked={s === entry}
                    onCheckedChange={() => setStart(s)}
                  >
                    <span className="flex w-full items-center justify-between gap-3">
                      {ChannelStates[s]?.label ?? s}
                      <span className="font-mono text-xs text-muted-foreground">
                        {s}
                      </span>
                    </span>
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {rows.map((state) => {
          const nexts = flow[state] ?? [];
          return (
            <div key={state} className="flex items-center gap-3 py-0.5">
              <span className="w-10 shrink-0" />
              <span className="flex w-16 shrink-0 justify-center">
                <ChannelStateChip state={state} />
              </span>
              {/* Raw state key, for reference when wiring flows/APIs. */}
              <span className="w-20 shrink-0 text-right font-mono text-xs text-muted-foreground">
                {state}
              </span>
              <div className="flex min-w-0 items-center gap-2">
                <span className="text-xs text-muted-foreground">→</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 max-w-72 justify-between gap-1 px-2 text-xs font-normal"
                    >
                      <span className="truncate">
                        {nexts.length
                          ? nexts
                              .map((s) => ChannelStates[s]?.label ?? s)
                              .join("、")
                          : "无可流转状态"}
                      </span>
                      <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-52">
                    <DropdownMenuLabel className="text-xs">
                      {ChannelStates[state]?.label ?? state} 可流转到
                    </DropdownMenuLabel>
                    {/* Whole catalog: picking an unused state pulls it in. */}
                    {stateKeys
                      .filter((s) => s !== state)
                      .map((s) => (
                        <DropdownMenuCheckboxItem
                          key={s}
                          checked={nexts.includes(s)}
                          // Keep the menu open so several targets can be
                          // toggled in one go.
                          onSelect={(e) => e.preventDefault()}
                          onCheckedChange={() => toggleNext(state, s)}
                        >
                          <span className="flex w-full items-center justify-between gap-3">
                            {ChannelStates[s]?.label ?? s}
                            <span className="font-mono text-xs text-muted-foreground">
                              {s}
                            </span>
                          </span>
                        </DropdownMenuCheckboxItem>
                      ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          );
        })}
      </div>
      {addingState && (
        // After creating, pick the new state in some state's nexts dropdown
        // to pull it into this app's flow (same as the version flow editor).
        <NewStateDialog onClose={() => setAddingState(false)} />
      )}
    </Section>
  );
};

export default ChannelFlowSection;
